import { VehicleController } from 'core/repositories/VehicleController';
import { exhaustiveCheck } from 'utility/exhaustiveCheck';
import { createListenerManager } from 'infrastructure/utility/ListenerManager';
import { ignore } from 'utility/ignore';

type ArrowKey = 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' | 'ArrowUp';

enum KeyState {
    Up, Down
}

type KeyLastEvent = {
    state: KeyState;
    startAt: number;
    interval?: number;
}

const CLOCK = 100; // [msec]
const DELTA = 0.5; // per sec

export class KeyboardVehicleController implements VehicleController {
    private readonly keys = {
        'ArrowRight': { state: KeyState.Down, startAt: Date.now() } as KeyLastEvent,
        'ArrowLeft': { state: KeyState.Down, startAt: Date.now() } as KeyLastEvent,
        'ArrowDown': { state: KeyState.Down, startAt: Date.now() } as KeyLastEvent,
        'ArrowUp': { state: KeyState.Down, startAt: Date.now() } as KeyLastEvent,
    };

    private accel = 0;
    private brake = 0;
    private steering = 0;

    addEventListener(listener: VehicleController.EventListener): void {
        this._listenerManager.addListener(listener);
    }

    removeEventListener(listener: VehicleController.EventListener): void {
        this._listenerManager.removeListener(listener);
    }

    readonly _listenerManager = createListenerManager<VehicleController.EventListener>(listeners => {
        const { keydownListener, keyupListener } = this._start(listeners);

        return () => {
            window.removeEventListener('keydown', keydownListener);
            window.removeEventListener('keyup', keyupListener);
        };
    });

    pushEvent(event: VehicleController.Event) {
        ignore(event);
    }

    private _start(listeners: VehicleController.EventListener[]) {
        const keydownListener = (ev: KeyboardEvent) => {
            switch (ev.key) {
                case 'ArrowLeft':
                case 'ArrowRight':
                case 'ArrowDown':
                case 'ArrowUp':
                    {
                        const key = ev.key;
                        const lastEvent = this.keys[key];
                        if (lastEvent.state != KeyState.Up) {
                            if (lastEvent.interval) clearInterval(lastEvent.interval);

                            const startAt = Date.now();
                            const interval = window.setInterval(() => {
                                this.update(key, KeyState.Up, listeners);
                            }, CLOCK);
                            this.keys[key] = { state: KeyState.Up, startAt, interval };
                            this.update(key, KeyState.Up, listeners);
                        }
                    }
                    break;
            }
        };

        const keyupListener = (ev: KeyboardEvent) => {
            switch (ev.key) {
                case 'ArrowLeft':
                case 'ArrowRight':
                case 'ArrowDown':
                case 'ArrowUp':
                    {
                        const key = ev.key;
                        const lastEvent = this.keys[key];
                        if (lastEvent.state != KeyState.Down) {
                            if (lastEvent.interval) clearInterval(lastEvent.interval);

                            const startAt = Date.now();
                            const interval = window.setInterval(() => {
                                this.update(key, KeyState.Down, listeners);
                            }, CLOCK);
                            this.keys[key] = { state: KeyState.Down, startAt, interval };
                            this.update(key, KeyState.Down, listeners);
                        }
                    }
                    break;
            }
        };

        window.addEventListener('keydown', keydownListener);
        window.addEventListener('keyup', keyupListener);

        return { keydownListener, keyupListener };
    }

    private update(key: ArrowKey, keyState: KeyState, listeners: VehicleController.EventListener[]) {
        switch (key) {
            case 'ArrowLeft':
            case 'ArrowRight':
                {
                    const l = this.keys['ArrowLeft'];
                    const r = this.keys['ArrowRight'];
                    if (l.state == KeyState.Up) {
                        if (r.state == KeyState.Up) {
                            if (l.startAt > r.startAt) {
                                this._update('ArrowLeft', true, listeners);
                            } else {
                                this._update('ArrowRight', true, listeners);
                            }
                        } else {
                            this._update('ArrowLeft', true, listeners);
                        }
                    } else {
                        if (r.state == KeyState.Up) {
                            this._update('ArrowRight', true, listeners);
                        } else {
                            this._update(this.steering > 0 ? 'ArrowRight' : 'ArrowLeft', false, listeners);
                        }
                    }
                }
                break;
            case 'ArrowDown':
            case 'ArrowUp':
                this._update(key, keyState == KeyState.Up, listeners);
                break;
            default:
                throw (exhaustiveCheck(key));
        }
    }

    private _update(key: ArrowKey, increase: boolean, listeners: VehicleController.EventListener[]) {
        const delta = DELTA * CLOCK / 1000;
        switch (key) {
            case 'ArrowRight':
                {
                    const prev = this.steering;
                    this.steering = Math.min(+1, Math.max(0, this.steering + (increase ? +delta : -delta)));
                    if (prev != this.steering) {
                        [...listeners].forEach(listener => {
                            listener({ eventType: VehicleController.EventType.Steering, steering: this.steering });
                        });
                    }
                }
                break;
            case 'ArrowLeft':
                {
                    const prev = this.steering;
                    this.steering = Math.max(-1, Math.min(-0, this.steering + (increase ? -delta : +delta)));
                    if (prev != this.steering) {
                        [...listeners].forEach(listener => {
                            listener({ eventType: VehicleController.EventType.Steering, steering: this.steering });
                        });
                    }
                }
                break;
            case 'ArrowDown':
                {
                    const prev = this.brake;
                    this.brake = Math.min(1, Math.max(0, this.brake + (increase ? +delta : -delta)));
                    if (prev != this.brake) {
                        [...listeners].forEach(listener => {
                            listener({ eventType: VehicleController.EventType.Brake, brake: this.brake });
                        });
                    }
                }
                break;
            case 'ArrowUp':
                {
                    const prev = this.accel;
                    this.accel = Math.min(1, Math.max(0, this.accel + (increase ? +delta : -delta)));
                    if (prev != this.accel) {
                        [...listeners].forEach(listener => {
                            listener({ eventType: VehicleController.EventType.Accel, accel: this.accel });
                        });
                    }
                }
                break;
            default:
                throw (exhaustiveCheck(key));
        }
    }
}
